import React from 'react';

import { compositeBedLabel } from '../../stores/bedInventoryStore';

/**
 * BedBoard (B3 / S7) — PURE, READ-ONLY render of the ward→room→bed(+bay)
 * inventory as a board. Wards are sections; ordinary rooms group their beds;
 * `is_bay` rooms render their beds DIRECTLY under the ward (never dropped /
 * mis-placed). Every bed is a VACANT / uncolored tile carrying the composite
 * label (ward · room/bay · bed) — NO live state, NO patients, NO BEDSTATE
 * reads, NO actions. EPIC C2 layers live occupancy onto THIS component, so it
 * takes only the derived tree (from `selectBoardTree`) and renders — it owns
 * no store, no fetch, no gate.
 *
 * @param {{ tree: Array, perWard: Object }} props
 *   tree    = selectBoardTree(state) output:
 *             [{ ward, rooms:[{room,beds[]}], bays:[{room,beds[]}] }]
 *   perWard = selectInventoryStats(state).perWard — { [ward_id]: { rooms, beds } }.
 *             The SINGLE source for the per-ward bed count (B7 / RC-1): this
 *             component no longer re-reduces the tree inline for a total.
 */
function BedTile({ ward, room, bed }) {
  return (
    <div
      className="bb-tile rounded-lg px-3 py-2 text-xs border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
      data-testid="bed-tile"
    >
      <div className="font-medium text-slate-800 dark:text-slate-100">
        {compositeBedLabel(ward, room, bed)}
      </div>
      <span className="inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">
        Vacant
      </span>
    </div>
  );
}

export default function BedBoard({ tree = [], perWard = {} }) {
  return (
    <div className="flex flex-col gap-4">
      {tree.map(({ ward, rooms, bays }) => {
        // B7 / RC-1 — per-ward room + bed counts come from the shared selector,
        // NOT an inline re-reduce of the tree (single source of truth). The room
        // tally EXCLUDES bays; bay BEDS are still counted. A ward absent from
        // `perWard` (e.g. deactivated ward under show-deactivated) gets NO count
        // line — never a fake "0 rooms · 0 beds" (forbidden fake-zero pattern).
        const wardStat = perWard[ward.ward_id];
        const hasBeds = !!wardStat && wardStat.beds > 0;
        return (
          <section
            key={ward.ward_id}
            className="rounded-xl border p-3 bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700"
          >
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">
              {ward.name}
              {wardStat && (
                <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400" data-testid="ward-count">
                  {wardStat.rooms} room{wardStat.rooms === 1 ? '' : 's'} · {wardStat.beds} bed{wardStat.beds === 1 ? '' : 's'}
                </span>
              )}
            </h3>

            {!hasBeds && (
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                No beds yet in this ward.
              </div>
            )}

            {/* Ordinary rooms — beds grouped under the room */}
            {rooms.map(({ room, beds }) => (
              <div key={room.room_id} className="mt-3">
                <div className="text-xs font-medium text-slate-600 dark:text-slate-300">{room.name}</div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {beds.map((bed) => (
                    <BedTile key={bed.bed_id} ward={ward} room={room} bed={bed} />
                  ))}
                </div>
              </div>
            ))}

            {/* Bays — beds render DIRECTLY under the ward (bay grouping) */}
            {bays.map(({ room, beds }) => (
              <div key={room.room_id} className="mt-3">
                <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  <span className="pcp-chip">Bay</span> {room.name}
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {beds.map((bed) => (
                    <BedTile key={bed.bed_id} ward={ward} room={room} bed={bed} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
